<script setup>
import { computed } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import AutomationSelect from './AutomationSelect.vue';
import TagsInput from './TagsInput.vue';

const props = defineProps({
  filters: { type: Array, default: () => [] },
  operator: { type: String, default: 'and' },
});
const emit = defineEmits(['update:filters', 'update:operator']);

const store = useStore();
const { t } = useI18n();

const labels = computed(() => store.getters['labels/getLabels'] ?? []);
const labelOptions = computed(() =>
  labels.value.map(l => ({ value: l.title, label: l.title, color: l.color }))
);

const filterTypeOptions = computed(() => [
  {
    value: 'has_all_tags',
    label: t('KANBAN.AUTOMATIONS.FILTERS.HAS_ALL_TAGS'),
  },
  {
    value: 'not_has_all_tags',
    label: t('KANBAN.AUTOMATIONS.FILTERS.NOT_HAS_ALL_TAGS'),
  },
  {
    value: 'days_before_date',
    label: t('KANBAN.AUTOMATIONS.FILTERS.DAYS_BEFORE_DATE'),
  },
  { value: 'on_date', label: t('KANBAN.AUTOMATIONS.FILTERS.ON_DATE') },
  {
    value: 'days_after_date',
    label: t('KANBAN.AUTOMATIONS.FILTERS.DAYS_AFTER_DATE'),
  },
]);

const usesTags = type => ['has_all_tags', 'not_has_all_tags'].includes(type);
const usesDays = type => ['days_before_date', 'days_after_date'].includes(type);

const unitOptions = computed(() => [
  { value: 'minutes', label: t('KANBAN.AUTOMATIONS.PANEL.UNIT_MINUTES') },
  { value: 'hours', label: t('KANBAN.AUTOMATIONS.PANEL.UNIT_HOURS') },
  { value: 'days', label: t('KANBAN.AUTOMATIONS.PANEL.UNIT_DAYS') },
  { value: 'months', label: t('KANBAN.AUTOMATIONS.PANEL.UNIT_MONTHS') },
  { value: 'years', label: t('KANBAN.AUTOMATIONS.PANEL.UNIT_YEARS') },
]);

function addFilter() {
  emit('update:filters', [
    ...props.filters,
    { type: 'has_all_tags', tags: [] },
  ]);
}

function updateFilter(index, patch) {
  const next = props.filters.map((f, i) =>
    i === index ? { ...f, ...patch } : f
  );
  emit('update:filters', next);
}

function changeType(index, type) {
  const base = { type };
  if (usesTags(type)) base.tags = [];
  if (usesDays(type)) {
    base.days = 1;
    base.unit = 'days';
  }
  const next = props.filters.map((f, i) => (i === index ? base : f));
  emit('update:filters', next);
}

function removeFilter(index) {
  emit(
    'update:filters',
    props.filters.filter((_, i) => i !== index)
  );
}
</script>

<template>
  <div class="flex flex-col gap-2">
    <template v-for="(filter, index) in filters" :key="index">
      <!-- Pílula E/OU entre filtros consecutivos -->
      <div v-if="index > 0" class="flex items-center justify-center">
        <div
          class="flex items-center rounded-lg border border-n-weak overflow-hidden text-xs font-semibold"
        >
          <button
            type="button"
            class="px-2.5 py-1 transition-colors"
            :class="
              operator === 'and'
                ? 'bg-n-brand text-white'
                : 'bg-n-background text-n-slate-10 hover:bg-n-alpha-1'
            "
            @click="emit('update:operator', 'and')"
          >
            {{ $t('KANBAN.AUTOMATIONS.OPERATOR_AND') }}
          </button>
          <button
            type="button"
            class="px-2.5 py-1 transition-colors border-l border-n-weak"
            :class="
              operator === 'or'
                ? 'bg-n-brand text-white'
                : 'bg-n-background text-n-slate-10 hover:bg-n-alpha-1'
            "
            @click="emit('update:operator', 'or')"
          >
            {{ $t('KANBAN.AUTOMATIONS.OPERATOR_OR') }}
          </button>
        </div>
      </div>

      <div class="flex flex-col gap-1.5">
        <div class="flex items-center gap-2">
          <AutomationSelect
            :model-value="filter.type"
            :options="filterTypeOptions"
            :placeholder="$t('KANBAN.AUTOMATIONS.IF')"
            @update:model-value="changeType(index, $event)"
          />
          <button
            type="button"
            class="text-n-ruby-11 hover:text-n-ruby-10"
            :aria-label="$t('KANBAN.AUTOMATIONS.DELETE')"
            @click="removeFilter(index)"
          >
            <span class="i-lucide-x size-3.5" />
          </button>
        </div>

        <div
          v-if="usesTags(filter.type) || usesDays(filter.type)"
          class="flex items-center gap-2"
        >
          <TagsInput
            v-if="usesTags(filter.type)"
            :model-value="filter.tags || []"
            :options="labelOptions"
            @update:model-value="updateFilter(index, { tags: $event })"
          />
          <template v-if="usesDays(filter.type)">
            <input
              type="number"
              min="0"
              :value="filter.days"
              class="reset-base !w-16 !h-8 shrink-0 grow-0 rounded-lg border border-n-weak bg-n-surface-2 pl-2 pr-1 text-sm text-n-slate-12 focus:outline-none appearance-auto"
              @input="
                updateFilter(index, { days: Number($event.target.value) })
              "
            />
            <AutomationSelect
              :model-value="filter.unit || 'days'"
              compact
              :options="unitOptions"
              @update:model-value="updateFilter(index, { unit: $event })"
            />
          </template>
        </div>
      </div>
    </template>

    <button
      type="button"
      class="self-start text-sm text-n-brand hover:opacity-80 font-medium"
      @click="addFilter"
    >
      {{ $t('KANBAN.AUTOMATIONS.ADD_FILTER') }}
    </button>
  </div>
</template>
